import XLSX from 'xlsx';
import React, { useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { 
  Formik, 
  Form, 
  Field,
} from 'formik';
import { Button } from '@pearsontechnology/aero-components';
import mercuryContext from 'ConnectionManager/mercuryContext';
import { KnowledgeMapColumnHeaders } from 'constants/knowledgeMap';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import Modal, { ModalActions } from 'components/Modal';
import { 
  getColumnHeaders, 
  validateColumnHeaders,
  validateKnowledgeMapCSVFileData,
  getKnowledgeMapCSVErrorMessage,
  insertKnowledgeMapItems,
  insertKnowledgeMapAssociation, 
  getKnowledgeMapWorksheetArray,
  deleteAllKnowledgeMapItems,
} from 'Workspace/Knowledge/utils';
import WorkspaceService from 'services/WorkspaceService';
import messages from './messages';
import {
  MAX_KNOWLEDGE_MAP_NAME_LENGTH,
  BINARY,
} from './constants';
import {
  ModalContent,
  Spacer,
  InputWithoutErrorWarningIcon,
  ImportFileDropZoneLabel,
  StyledDiv,
  StyledSpan,
  StyledInput,
} from './styles';
import KnowledgeMapImportFileDropZone from '../KnowledgeMapImportFileDropZone';
import { createKnowledgeMap } from '../../services/index';
 
const ImportModal = ({
  visible,
  onDismiss,
  toast,
  isReplace,
  toBeReplacedKnowledgeMap,
}) => {
  const [isLoading] = useState(false);
  const [isCloseEnable] = useState(true);
  const [staticBackdrop] = useState(false);
  const [knowledgeMapImportErrorMessageList, setKnowledgeMapImportErrorMessageList] = useState([]);
  const [importEnabled, setImportEnabled] = useState(false);
  const [knowledgeMapName, getKnowledgeMapName] = useState('');
  const { apolloClient } = useContext(mercuryContext);

  const resetValidationErrorList = useCallback(() => {
    setKnowledgeMapImportErrorMessageList([]);
  }, []);
  
  const validateFields = ({ knowledgeMapName, file }) => {
    if (isReplace) {
      !file ? setImportEnabled(false) : setImportEnabled(true);
      return;
    }
    const errors = {},
      knowledgeMapNameExceedLimit = knowledgeMapName.length > MAX_KNOWLEDGE_MAP_NAME_LENGTH;
    if (!knowledgeMapName || !file || knowledgeMapNameExceedLimit) {
      setImportEnabled(false);
    } else {
      getKnowledgeMapName(knowledgeMapName);
      setImportEnabled(true);
    }

    if (knowledgeMapNameExceedLimit) {
      errors.knowledgeMapName = intl.formatMessage(messages.KnowledgeMapLengthError);
    } 
    return errors;
  };

  const validateFile = ({file}) =>{
    /* Boilerplate to set up FileReader */
    const knowledgeMapCSVReader = new FileReader();
    knowledgeMapCSVReader.onload = async (event) => {
      /* Parse data */
      const knowledgeMapCSVResultStaring = event.target.result;
      const workSheet = XLSX.read(knowledgeMapCSVResultStaring, {
        type: BINARY,
        bookVBA: true,
        raw: true,
      });
      /* Get first worksheet */
      const knowledgeMapWorksheetName = workSheet.SheetNames[0];
      const knowledgeMapWorksheet = workSheet.Sheets[knowledgeMapWorksheetName];
      const knowledgeMapCSVColumnHeaders = getColumnHeaders(knowledgeMapWorksheet);
      const isColumnHeaders = validateColumnHeaders(KnowledgeMapColumnHeaders,knowledgeMapCSVColumnHeaders);
      const knowledgeMapCSVObjectArray = validateKnowledgeMapCSVFileData(knowledgeMapWorksheet);
      const knowledgeMapErrorMessageArray = getKnowledgeMapCSVErrorMessage(knowledgeMapCSVObjectArray, isColumnHeaders);
      const knowledgeMapWorkSheetResult = XLSX.utils.sheet_to_json(knowledgeMapWorksheet);
      const knowledgeMapWorkSheetObjectArray = getKnowledgeMapWorksheetArray(knowledgeMapWorkSheetResult);

      if (isColumnHeaders && knowledgeMapWorkSheetResult.length > 0 && knowledgeMapErrorMessageArray.length === 0){
        setImportEnabled(false);
        setKnowledgeMapImportErrorMessageList([]);
        const workspaceId = WorkspaceService.getSelectedWorkspace().id;
        let knowledgeMapId = null;
        
        if (isReplace) {
          const { documentId, items } = toBeReplacedKnowledgeMap;
          knowledgeMapId = documentId;
          await deleteAllKnowledgeMapItems(items, knowledgeMapId, apolloClient);
        } else {
          const KnowledgeMap = await createKnowledgeMap(
            workspaceId,
            knowledgeMapName,
            apolloClient
          );
          knowledgeMapId = KnowledgeMap.documentId;
        }
  
        const insertedKnowledgeMapItem = await insertKnowledgeMapItems(knowledgeMapWorkSheetObjectArray, knowledgeMapId, apolloClient);

        insertKnowledgeMapAssociation(insertedKnowledgeMapItem, knowledgeMapId, apolloClient);
        onDismiss();
        toast({
          message: isReplace ? intl.formatMessage(messages.knowledgeMapReplaceSuccess) 
            : intl.formatMessage(messages.knowledgeMapUploadSuccess),
        });
      } else {
        setKnowledgeMapImportErrorMessageList(knowledgeMapErrorMessageArray);
        setImportEnabled(false);
      }
    };
    knowledgeMapCSVReader.readAsBinaryString(file);
  };
  
  const onSubmit = ({ knowledgeMapName, file }) => {
    validateFile({
      knowledgeMapName: knowledgeMapName.trim(),
      file});
  };

  const KnowledgeUploadModal = () => (
    <Formik
      onSubmit={onSubmit}
      validate={validateFields}
      validateOnChange
      validateOnBlur
      initialValues={{ knowledgeMapName: '', file: undefined }}
    >
      <Form>
        <ModalContent>
          {!isReplace && 
          <Field name="knowledgeMapName">
            {({ field, form }) => (
              <InputWithoutErrorWarningIcon>
                <StyledInput
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  label={intl.formatMessage(messages.knowledgeMapName)}
                  placeholder={intl.formatMessage(
                    messages.KnowledgeMapPlaceholder
                  )}
                  feedback={`${form.errors[field.name] || ''}`}
                  disabled={isLoading}
                />
                <StyledDiv>
                  <StyledSpan
                    hasError={field.value.length > MAX_KNOWLEDGE_MAP_NAME_LENGTH}
                  >
                    {field.value.length}
                  </StyledSpan>
                /{MAX_KNOWLEDGE_MAP_NAME_LENGTH}
                </StyledDiv>
              </InputWithoutErrorWarningIcon>
            )}
          </Field>}
          <Spacer />
          <Field name="file">
            {({ field, form }) => (
              <>
                <ImportFileDropZoneLabel>
                  {intl.formatMessage(messages.importFile)}
                </ImportFileDropZoneLabel>
                <KnowledgeMapImportFileDropZone
                  error={form.errors[field.name]}
                  disabled={isLoading}
                  setFieldValue={form.setFieldValue}
                  resetValidationErrorList={resetValidationErrorList}
                  fieldName="file"
                  knowledgeMapImportErrorMessageList={knowledgeMapImportErrorMessageList}
                />
                <ImportFileDropZoneLabel>
                  {intl.formatMessage(messages.importFiletype)}
                </ImportFileDropZoneLabel>
              </>
            )}
          </Field>
        </ModalContent>
        <ModalActions>
          <Button grey onClick={onDismiss}>
            {intl.formatMessage(messages.cancel)}
          </Button>
          <Button
            disabled={!importEnabled}
            primary
            loadable
            isLoading={isLoading}
            type="submit"
          >
            {isReplace ? intl.formatMessage(messages.replace) : intl.formatMessage(messages.import)}
          </Button>
        </ModalActions>
      </Form>
    </Formik>
  );

  return (
    <Modal
      closeOnOuterClick={!staticBackdrop}
      showCloseIcon={isCloseEnable}
      onDismiss={onDismiss}
      visible={visible}
      title={
        isReplace ? 
          intl.formatMessage(messages.replaceKnowledgeMap) :
          intl.formatMessage(messages.importKnowledgeMap) 
      }
    >
      {KnowledgeUploadModal()}
    </Modal>
  );
};

ImportModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  toast: PropTypes.func.isRequired,
};

export default ImportModal;
